// Split-scroll table wrapper — lets a single data table be dragged into TWO
// side-by-side panes, each with its OWN independent horizontal scrollbar while
// they share a single (synchronised) vertical scroll. Used by every list /
// spreadsheet surface (designer live-preview, in-app Run, downloaded runtime)
// so the behaviour is identical everywhere.
//
// How it works
// ------------
// A real HTML <table> is one layout box with one horizontal scroll position,
// so two *independent* horizontal scroll zones fundamentally need two tables.
// This wrapper renders the host's table twice via a render-prop — once for the
// left column subset, once for the right subset — and the host decides, from
// the `range` it is handed, which data columns and which gutters to emit. When
// the table is NOT split (the default) the host renders exactly one table, byte
// for byte what it rendered before this wrapper existed, so existing behaviour
// is untouched until the user drags the splitter.
//
// The split position is the number of DATA columns in the left pane. It is
// remembered per `storageKey` in localStorage (a personal, view-only
// preference — never written to the saved design).
//
// Host contract
// -------------
//   <SplitScrollTable storageKey={`split:${obj}`} dataCount={cols.length}>
//     {range => (
//       <table>
//         <colgroup>… leading gutter iff range.isLeading … data cols
//                  cols.slice(range.colStart, range.colEnd) …
//                  trailing gutter iff range.isTrailing …</colgroup>
//         …
//       </table>
//     )}
//   </SplitScrollTable>
//
// Each DATA column's <th> must carry `data-split-col="<dataIndex>"` (0-based,
// in the host's column order) so the wrapper can measure column widths and snap
// the splitter to real column edges. Optionally each body <tr> can carry
// `data-split-row="<key>"` so rows that differ in height between the two panes
// (e.g. an expanded detail row) are height-matched; without it rows are paired
// by position, which is correct for the common uniform-height case.

import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState,
} from 'react'

/** Imperative control surface for a SplitScrollTable (e.g. a "Freeze column"
 *  menu item). Obtain it by passing a `ref`. */
export interface SplitScrollHandle {
  /** Freeze the first `cols` data columns into the left pane (default 1); the
   *  user can then drag the splitter to move or remove it. */
  freeze: (cols?: number) => void
  /** Collapse back to a single scroll zone. */
  unfreeze: () => void
  /** Whether the table is currently split. */
  isFrozen: () => boolean
}

export interface SplitRange {
  /** Render the leading gutter (select / status / line-no / expand) in this pane. */
  isLeading: boolean
  /** Render the trailing gutter (per-row actions) in this pane. */
  isTrailing: boolean
  /** Not split — render every column + both gutters (back-compat single table). */
  whole: boolean
  /** First data-column index this pane renders (inclusive). */
  colStart: number
  /** One past the last data-column index this pane renders (exclusive). */
  colEnd: number
}

export interface SplitScrollTableProps {
  /** Number of splittable DATA columns (gutters excluded). */
  dataCount: number
  /** Render a <table> for the given column range. Called once (whole) or twice. */
  children: (range: SplitRange) => React.ReactNode
  /** Persist the split position per object. Omit to keep it ephemeral. */
  storageKey?: string
  /** Fewest data columns the left pane may hold once split (default 1). */
  minIndex?: number
  /** Disable splitting entirely — render one plain scroll pane, no handle. */
  disabled?: boolean
  /** Data columns frozen by default — the organization's customization
   *  (`frozen_columns`). A person's own split (storageKey) still wins. */
  defaultFrozen?: number
  /** Called with the new frozen column count after the user moves the split
   *  (drag end, freeze, unfreeze). Customize mode saves it for everyone. */
  onSplitChange?: (cols: number) => void
  style?: React.CSSProperties
  className?: string
}

interface Persisted { i: number; w: number }

function readPersisted(key?: string): Persisted | null {
  if (!key || typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const v = JSON.parse(raw)
    if (v && typeof v.i === 'number' && typeof v.w === 'number' && v.i > 0) return v
  } catch { /* ignore corrupt / unavailable storage */ }
  return null
}

function writePersisted(key: string | undefined, v: Persisted | null) {
  if (!key || typeof window === 'undefined') return
  try {
    if (v && v.i > 0) window.localStorage.setItem(key, JSON.stringify(v))
    else window.localStorage.removeItem(key)
  } catch { /* ignore */ }
}

const SPLITTER_W = 7

// Monotonic-ish clock for the equalize debounce; falls back to 0 where
// `performance` is unavailable (the debounce then simply doesn't suppress).
function now(): number {
  return typeof performance !== 'undefined' && performance.now ? performance.now() : 0
}

export const SplitScrollTable = forwardRef<SplitScrollHandle, SplitScrollTableProps>(function SplitScrollTable({
  dataCount, children, storageKey, minIndex = 1, disabled, style, className,
  defaultFrozen = 0, onSplitChange,
}: SplitScrollTableProps, ref) {
  const persisted = useRef<Persisted | null>(null)
  if (persisted.current === null) {
    persisted.current = readPersisted(storageKey)
      // No personal split: start from the organization's frozen columns (the
      // width is measured on the first layout, below).
      ?? (defaultFrozen > 0 ? { i: defaultFrozen, w: 0 } : null)
  }
  const onSplitChangeRef = useRef(onSplitChange)
  onSplitChangeRef.current = onSplitChange

  // `splitIndex` = data columns in the left pane (0 = not split).
  // `leftWidth`  = the visible (flex-basis) width of the left pane in px; the
  //               left content may be wider, in which case the left pane scrolls.
  const initial = !disabled ? persisted.current : null
  const [splitIndex, setSplitIndex] = useState(initial ? Math.min(initial.i, dataCount) : 0)
  const [leftWidth, setLeftWidth] = useState(initial ? initial.w : 0)
  const [dragging, setDragging] = useState(false)
  // Raw cursor x during a drag — drives the live guide line so the gesture
  // reads as responsive from the very first pixel (even while still inside the
  // fixed leading gutter, before the split snaps to a column boundary).
  const [dragX, setDragX] = useState<number | null>(null)

  const outerRef = useRef<HTMLDivElement>(null)
  const leftRef = useRef<HTMLDivElement>(null)
  const rightRef = useRef<HTMLDivElement>(null)

  // Measured DATA-column widths + leading-gutter width, kept fresh by a
  // ResizeObserver. The drag handler reads these (not React state) so snapping
  // stays in sync with live column resizes without re-rendering on every frame.
  const metrics = useRef<{ widths: number[]; leading: number }>({ widths: [], leading: 0 })
  // Timestamp of the last row-height equalize, used to debounce the
  // ResizeObserver against the resize our own height writes trigger.
  const lastEqualizeAt = useRef(0)

  const measure = useCallback(() => {
    const root = outerRef.current
    if (!root) return
    const ths = Array.from(
      root.querySelectorAll<HTMLElement>('thead th[data-split-col]'),
    )
    const widths: number[] = []
    for (const th of ths) {
      const idx = Number(th.getAttribute('data-split-col'))
      if (!Number.isFinite(idx)) continue
      widths[idx] = th.offsetWidth
    }
    // Leading-gutter width = sum of header cells before the first data column in
    // whichever pane owns the leading gutter (the left pane when split, else the
    // single pane).
    let leading = 0
    const firstData = ths.find(t => Number(t.getAttribute('data-split-col')) === 0)
    if (firstData) {
      const headRow = firstData.parentElement
      if (headRow) {
        for (const cell of Array.from(headRow.children) as HTMLElement[]) {
          if (cell === firstData) break
          leading += cell.offsetWidth
        }
      }
    }
    metrics.current = { widths, leading }
    // Publish the active scroll pane's visible width as a CSS variable so
    // descendants (e.g. an expanded child-detail panel that pins itself with
    // position:sticky) can size to the pane they actually live in instead of
    // the full, wider table — otherwise their toolbar runs off the pane.
    const rp = rightRef.current
    if (rp) root.style.setProperty('--ss-viewport', `${rp.clientWidth}px`)
  }, [])

  // Pair the two panes' rows and give each pair the taller height, so the
  // header, every body row, and any footer stay aligned across the panes
  // (otherwise a header/row that's taller on one side shifts everything below
  // it out of sync). Body rows pair by `data-split-row` when available (handles
  // the detail-row spacer); header/footer pair by position. No-op when not split.
  const equalizeRows = useCallback(() => {
    const left = leftRef.current
    const right = rightRef.current
    if (!left || !right) return

    const pairs: Array<[HTMLElement, HTMLElement]> = []
    // Header + footer: a single positional pairing (each section has matching
    // row counts across panes).
    for (const sect of ['thead', 'tfoot']) {
      const lr = Array.from(left.querySelectorAll<HTMLElement>(`${sect} > tr`))
      const rr = Array.from(right.querySelectorAll<HTMLElement>(`${sect} > tr`))
      const n = Math.min(lr.length, rr.length)
      for (let i = 0; i < n; i++) pairs.push([lr[i], rr[i]])
    }
    // Body: prefer keyed pairing so the left detail-row spacer lines up with the
    // right child grid; fall back to position for uniform rows.
    const lRows = Array.from(left.querySelectorAll<HTMLElement>('tbody > tr'))
    const rRows = Array.from(right.querySelectorAll<HTMLElement>('tbody > tr'))
    const byKey = new Map<string, HTMLElement>()
    let keyed = true
    for (const r of rRows) {
      const k = r.getAttribute('data-split-row')
      if (!k) { keyed = false; break }
      byKey.set(k, r)
    }
    if (keyed) {
      for (const l of lRows) {
        const k = l.getAttribute('data-split-row')
        const r = k ? byKey.get(k) : undefined
        if (r) pairs.push([l, r])
      }
    } else {
      const n = Math.min(lRows.length, rRows.length)
      for (let i = 0; i < n; i++) pairs.push([lRows[i], rRows[i]])
    }

    for (const [l, r] of pairs) { l.style.height = ''; r.style.height = '' }
    for (const [l, r] of pairs) {
      const h = Math.max(l.offsetHeight, r.offsetHeight)
      l.style.height = `${h}px`
      r.style.height = `${h}px`
    }
    // Stamp so the ResizeObserver can ignore the resize OUR OWN height writes
    // trigger (clearing the spacer back to its natural height would otherwise
    // re-fire the observer and loop). Genuine later changes fall outside this
    // window and are still picked up.
    lastEqualizeAt.current = now()
  }, [])

  // Keep metrics + row heights fresh on every render (data / split / width
  // changes are all render-driven).
  useLayoutEffect(() => {
    measure()
    // A split restored from `defaultFrozen` has no width yet: size the left
    // pane to exactly its frozen columns.
    if (splitIndex > 0 && leftWidth <= 0) {
      const { widths, leading } = metrics.current
      let w = leading
      for (let k = 0; k < splitIndex; k++) w += widths[k] || 160
      if (w > 0) setLeftWidth(w)
    }
    if (splitIndex > 0) equalizeRows()
  })

  // The organization's frozen columns changed (customized) and the person has
  // no split of their own → follow it.
  const lastDefault = useRef(defaultFrozen)
  useEffect(() => {
    if (lastDefault.current === defaultFrozen) return
    lastDefault.current = defaultFrozen
    if (readPersisted(storageKey)) return
    setSplitIndex(Math.max(0, Math.min(defaultFrozen, dataCount)))
    setLeftWidth(0)
  }, [defaultFrozen, storageKey, dataCount])

  // Catch changes that don't re-render this component — window resizes, live
  // column resizes, and async content growth (e.g. a child detail grid loading
  // its rows in the right pane after the row first painted).
  useEffect(() => {
    const root = outerRef.current
    if (!root || typeof ResizeObserver === 'undefined') return
    let raf = 0
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        measure() // read-only — never loops
        if (splitIndex > 0 && now() - lastEqualizeAt.current > 80) equalizeRows()
      })
    })
    ro.observe(root)
    return () => { cancelAnimationFrame(raf); ro.disconnect() }
  }, [measure, equalizeRows, splitIndex])

  // If the column count shrinks below the saved split, clamp it.
  useEffect(() => {
    if (splitIndex > dataCount) setSplitIndex(dataCount)
  }, [dataCount, splitIndex])

  // ── Synchronised vertical scroll (right pane is the master scroller) ──────
  const syncing = useRef(false)
  const onRightScroll = useCallback(() => {
    const l = leftRef.current; const r = rightRef.current
    if (!l || !r || syncing.current) return
    syncing.current = true
    l.scrollTop = r.scrollTop
    syncing.current = false
  }, [])
  // The left pane has no vertical scrollbar (overflow-y hidden); forward wheel
  // gestures over it to the master scroller so rows still scroll there.
  useEffect(() => {
    const l = leftRef.current
    if (!l || splitIndex <= 0) return
    const onWheel = (e: WheelEvent) => {
      const r = rightRef.current
      if (!r) return
      // Only hijack vertical intent; let horizontal wheel scroll the left zone.
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return
      r.scrollTop += e.deltaY
      l.scrollTop = r.scrollTop
      e.preventDefault()
    }
    l.addEventListener('wheel', onWheel, { passive: false })
    return () => l.removeEventListener('wheel', onWheel)
  }, [splitIndex])

  // ── Drag (both the docked handle and the middle splitter share this) ──────
  const beginDrag = useCallback((startEvt: React.MouseEvent) => {
    if (disabled) return
    startEvt.preventDefault()
    setDragging(true)
    measure()
    // Seed the guide at the press point so the line appears on mousedown.
    const startRect = outerRef.current?.getBoundingClientRect()
    if (startRect) setDragX(Math.max(0, Math.min(startEvt.clientX - startRect.left, startRect.width)))

    // Track the latest committed position so `up` can persist it without
    // chasing async state.
    let lastI = 0
    let moved = false
    let lastW = 0
    const move = (e: MouseEvent) => {
      const root = outerRef.current
      if (!root) return
      moved = true
      const rect = root.getBoundingClientRect()
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
      const { widths, leading } = metrics.current
      // Live guide follows the cursor from the first pixel so the drag always
      // reads as active, even while still over the fixed leading gutter.
      setDragX(x)
      // Snap: count data columns whose LEFT edge sits left of the cursor, so a
      // column the cursor is partway through stays in the left pane (and that
      // pane then scrolls to reveal its tail).
      let idx = 0
      let edge = leading
      for (let k = 0; k < dataCount; k++) {
        if (edge < x) idx = k + 1
        edge += widths[k] || 0
      }
      // Collapse back to a single pane only in a slim zone at the very left, so
      // any real rightward drag immediately forms the split (the left pane then
      // scrolls within its width — it need not be as wide as the leading gutter).
      const collapseAt = Math.max(8, Math.min(36, leading * 0.5))
      if (x < collapseAt) {
        lastI = 0; lastW = 0
        setSplitIndex(0)
        setLeftWidth(0)
        return
      }
      idx = Math.max(minIndex, Math.min(idx, dataCount))
      const maxW = rect.width - 120 // always leave room for the right pane
      const w = Math.max(40, Math.min(x, maxW))
      lastI = idx; lastW = w
      setSplitIndex(idx)
      setLeftWidth(w)
    }
    const up = () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      setDragging(false)
      setDragX(null)
      writePersisted(storageKey, { i: lastI, w: lastW })
      if (moved) onSplitChangeRef.current?.(lastI)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }, [disabled, dataCount, minIndex, storageKey, measure])

  // Imperative control (e.g. a "Freeze column" menu item driving the split).
  useImperativeHandle(ref, () => ({
    freeze: (cols = 1) => {
      if (disabled || dataCount < 1) return
      measure()
      const { widths, leading } = metrics.current
      const n = Math.max(1, Math.min(cols, dataCount))
      let w = leading
      for (let k = 0; k < n; k++) w += widths[k] || 160
      setSplitIndex(n)
      setLeftWidth(w)
      writePersisted(storageKey, { i: n, w })
      onSplitChangeRef.current?.(n)
    },
    unfreeze: () => {
      setSplitIndex(0)
      setLeftWidth(0)
      writePersisted(storageKey, null)
      onSplitChangeRef.current?.(0)
    },
    isFrozen: () => splitIndex > 0,
  }), [disabled, dataCount, storageKey, measure, splitIndex])

  const split = splitIndex > 0 && splitIndex < dataCount + 1 && dataCount >= 2

  // `overscrollBehaviorX: 'contain'` stops a sideways trackpad swipe that hits
  // the end of a pane from bubbling up and triggering browser back/forward
  // navigation — a common annoyance when scrolling wide tables horizontally.
  const paneBase: React.CSSProperties = {
    minHeight: 0, position: 'relative', overscrollBehaviorX: 'contain',
  }

  if (!split) {
    // Single pane — identical to the pre-split layout, plus a slim docked grab
    // handle on the left edge that the user drags right to create the split.
    return (
      <div
        ref={outerRef}
        className={className}
        style={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative', ...style }}
      >
        <div
          ref={rightRef}
          style={{ ...paneBase, flex: 1, overflow: 'auto' }}
        >
          {children({ isLeading: true, isTrailing: true, whole: true, colStart: 0, colEnd: dataCount })}
        </div>
        {!disabled && dataCount >= 2 && (
          <DockHandle onMouseDown={beginDrag} active={dragging} />
        )}
        {dragging && dragX != null && <DragGuide x={dragX} />}
        {dragging && <DragShield />}
      </div>
    )
  }

  return (
    <div
      ref={outerRef}
      className={className}
      style={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative', ...style }}
    >
      <div
        ref={leftRef}
        style={{
          ...paneBase,
          flex: `0 0 ${leftWidth}px`, width: leftWidth,
          overflowX: 'auto', overflowY: 'hidden',
        }}
      >
        {children({ isLeading: true, isTrailing: false, whole: false, colStart: 0, colEnd: splitIndex })}
      </div>
      <div
        onMouseDown={beginDrag}
        title="Drag to move the split · drag fully left to merge back into one scroll zone"
        style={{
          flex: `0 0 ${SPLITTER_W}px`, width: SPLITTER_W, cursor: 'col-resize',
          background: dragging ? 'var(--color-accent, #6366f1)' : 'var(--color-border, #d1d5db)',
          position: 'relative', zIndex: 3,
          backgroundClip: 'content-box',
          borderLeft: '2px solid transparent', borderRight: '2px solid transparent',
        }}
      >
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: 3, height: 26, borderRadius: 2,
          background: dragging ? 'rgba(255,255,255,0.9)' : 'var(--color-text-secondary, #6b7280)',
          opacity: 0.7, pointerEvents: 'none',
        }} />
      </div>
      <div
        ref={rightRef}
        onScroll={onRightScroll}
        style={{ ...paneBase, flex: 1, minWidth: 0, overflow: 'auto' }}
      >
        {children({ isLeading: false, isTrailing: true, whole: false, colStart: splitIndex, colEnd: dataCount })}
      </div>
      {dragging && dragX != null && <DragGuide x={dragX} />}
      {dragging && <DragShield />}
    </div>
  )
})

// A live vertical guide that tracks the cursor during a drag, so the gesture
// reads as responsive even before the split snaps to a column boundary.
function DragGuide({ x }: { x: number }) {
  return (
    <div style={{
      position: 'absolute', top: 0, bottom: 0, left: x, width: 2,
      marginLeft: -1, background: 'var(--color-accent, #6366f1)',
      zIndex: 6, pointerEvents: 'none',
      boxShadow: '0 0 0 1px rgba(99,102,241,0.25)',
    }} />
  )
}

// Slim grab handle docked at the left edge when the table is not yet split.
function DockHandle({ onMouseDown, active }: {
  onMouseDown: (e: React.MouseEvent) => void
  active: boolean
}) {
  const [hover, setHover] = useState(false)
  return (
    <div
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title="Drag right to split the table into two horizontal scroll zones"
      style={{
        position: 'absolute', top: 0, bottom: 0, left: 0, width: 10,
        cursor: 'col-resize', zIndex: 4,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div style={{
        width: active || hover ? 4 : 3, height: active || hover ? 40 : 24,
        borderRadius: 3,
        background: active
          ? 'var(--color-accent, #6366f1)'
          : hover ? 'var(--color-accent, #6366f1)' : 'var(--color-border, #cbd5e1)',
        opacity: active || hover ? 0.9 : 0.55,
        transition: 'height 0.12s ease, opacity 0.12s ease',
      }} />
    </div>
  )
}

// Transparent full-window shield during a drag so the pointer keeps the
// col-resize cursor and text selection / iframe capture don't interfere.
function DragShield() {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999, cursor: 'col-resize',
      userSelect: 'none',
    }} />
  )
}

export default SplitScrollTable
